import { useState } from 'react';
import { TextField } from '@mui/material';
import { HexColorPicker } from 'react-colorful';
import { sitePalette as PALETTE } from '../../../planning/colors';
import { SITE_MODAL_LABELS as LABELS } from '../../../../utils/labels';

const HEX_REGEX = /^#[0-9a-fA-F]{6}$/;

interface ColorPickerProps {
  value: string;
  onChange: (color: string) => void;
}

export default function ColorPicker({ value, onChange }: ColorPickerProps) {
  const [inputValue, setInputValue] = useState(value);

  const handlePickerChange = (color: string) => {
    setInputValue(color);
    onChange(color);
  };

  const handleSwatchClick = (color: string) => {
    setInputValue(color);
    onChange(color);
  };

  const handleInputChange = (raw: string) => {
    const v = raw.startsWith('#') ? raw : `#${raw}`;
    setInputValue(v);
    if (HEX_REGEX.test(v)) onChange(v);
  };

  const previewColor = HEX_REGEX.test(inputValue) ? inputValue : value;

  return (
    <div className="flex flex-col gap-2">
      <HexColorPicker color={previewColor} onChange={handlePickerChange} className="w-full" />
      <div className="flex flex-wrap gap-x-2 mt-2 justify-center">
        {PALETTE.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => handleSwatchClick(c)}
            aria-label={c}
            className={`w-6 h-6 rounded-full border-0 transition-all ${
              value === c
                ? 'outline outline-2 outline-offset-2 outline-foreground scale-110'
                : 'hover:scale-105'
            }`}
            style={{ backgroundColor: c }}
          />
        ))}
      </div>
      <div className="flex items-center gap-2 mt-4">
        <span
          className="w-6 h-6 rounded-full shrink-0 border border-border"
          style={{ backgroundColor: previewColor }}
        />
        <TextField
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          size="small"
          inputProps={{ maxLength: 7 }}
          className="flex-1"
          label={LABELS.hexLabel}
          error={!HEX_REGEX.test(inputValue)}
          helperText={!HEX_REGEX.test(inputValue) ? LABELS.hexError : undefined}
        />
      </div>
    </div>
  );
}
